import { Box } from '@mui/system';
import React, { useState } from 'react';
import AtlasV2MetricListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';

import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { BEACON_KEY_METRIC_CARD_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import useRetailSalesSortedSkus from 'src/serverProxy/useRetailSalesSortedSkus';
import { useAppSelector } from 'src/utils/Hooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';

const fields = [
  {
    name: 'reviewsRating',
    displayName: 'RATING'
  },
  {
    name: 'reviewsCount',
    displayName: 'REVIEWS COUNT'
  },
  {
    name: 'retailSales',
    displayName: 'RETAIL SALES'
  }
];

/**
 * Shows the ratings for each product in a grid,
 * sorted by retail sales
 */
export default function AtlasV2RatingsProductGrid() {
  const [page, setPage] = useState(1);
  const weekEndingFields = fields.map(removeComparisonFormatter);

  // Get SKUs sorted by retail sales so we can use it as a condition for
  // fetching the ratings
  const {
    isLoading: skusLoading,
    skuIndexMap,
    slicedSkus
  } = useRetailSalesSortedSkus({
    pageNumber: page,
    aggregatePages: true
  });
  const endWeek = useAppSelector((state) => state.mainTimePeriod.endWeek);
  const { isProductEntity } = useProductEntity();

  return (
    <Box sx={{ marginTop: `${BEACON_KEY_METRIC_CARD_SPACING}px` }}>
      {isProductEntity ? (
        <AtlasV2MetricListContainer
          entityTableHeaderProps={{
            title: 'Rating by Week'
          }}
          indexName="sales"
          sortFieldName="reviewsRating"
          fields={weekEndingFields}
          headColumnDefinition={getWeekColumnDefinition({
            getTableColumnProps(defaults) {
              return {
                ...defaults,
                style: {
                  ...defaults.style,
                  width: '220px'
                }
              };
            }
          })}
          postProcessData={(data) => {
            return data.sort((a, b) => b.weekId.localeCompare(a.weekId));
          }}
          useTableDataProps={{
            groupByFieldName: 'weekId',
            filterResponse: () => true,
            itemsPerPage: 100
          }}
          showGrid={false}
        />
      ) : (
        <AtlasV2ProductListContainer
          loading={skusLoading}
          entityTableHeaderProps={{
            title: 'Rating by Product'
          }}
          headColumnDefinition={{
            headerName: 'Products',
            valueFormatter: (_, row: { product: any }) => (
              <div style={{ width: '559px' }}>
                <ProductCell
                  key={row.product.stacklineSku}
                  stacklineSku={row.product.stacklineSku}
                  title={row.product.title}
                />
              </div>
            )
          }}
          fields={fields}
          indexName="sales"
          sortFieldName="reviewsRating"
          useTableDataProps={{
            startWeekId: endWeek, // We only want to show ratings for the current week
            useGenericAdvancedSearchArgs: {
              shouldPerformFetch: slicedSkus.length > 0 && !skusLoading
            },
            buildRequest: (builder) => {
              return builder.addConditionTermFilter('stacklineSku', 'should', slicedSkus);
            },
            pageNumber: 1
          }}
          postProcessData={(data: { product: ProductEntity }[]) => {
            const res = [...data].sort((a, b) => {
              const aIndex = skuIndexMap[a.product.stacklineSku.toLowerCase()];
              const bIndex = skuIndexMap[b.product.stacklineSku.toLowerCase()];
              return aIndex - bIndex;
            });
            return res;
          }}
          onChangePage={(args) => {
            setPage(args.chunkedPage);
          }}
          appTableProps={{
            sx: {
              'tr td:first-child': {
                width: '624px',
                paddingRight: '40px'
              }
            }
          }}
        />
      )}
    </Box>
  );
}
